import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { loginConfirmLoginEmailChallenge } from '@column/api';

import { Logo } from '../_components';
import { ROUTE as APP_ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { LoginAuthMethod, shouldPromptMFASetup } from '~/lib/session';
import { type ResumeMFAState } from '~/public/pages/Login';
import { ONBOARDING_QUERY_PARAM, ROUTE } from '~/public/routes';
import { ChallengeName, EmailConfirmationStatus, MFAStatus, getActivePlaidAuthFlow } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Paragraph, Text } from '~/styles';
import { delay } from '~/util';

import { claimTokenRedemption, releaseTokenRedemption, wasRejectedByServer } from './tokenReplayGuard';

interface Params {
  code?: string;
}

const REFERRER_POLICY_META_ID = 'magic-link-referrer-policy';
const MINIMUM_AUTHENTICATION_DURATION_MS = 1500;
const COMPLETION_ANIMATION_DURATION_MS = 1200;
const COMPLETION_RIPPLE_DURATION_MS = 800;
const COMPLETION_RIPPLE_OUT_FEATHER = 0.4;

const DOT_GRID_SIZE = 8;
// Fixed so the gap between the centered content and the dot ring never changes with the viewport.
const DOT_FIELD_SIZE = 1280;
const DOT_FIELD_LINES = Math.ceil(DOT_FIELD_SIZE / DOT_GRID_SIZE);
const RECAPTCHA_CENTER_FROM_VIEWPORT_BOTTOM = 40;
const TWINKLE_FRAME_INTERVAL_MS = 50;

type CompletionState = 'idle' | 'success' | 'error';

const dotRandom = (row: number, column: number, salt: number): number => {
  const value = Math.sin(row * 12.9898 + column * 78.233 + salt * 37.719) * 43758.5453;
  return value - Math.floor(value);
};

const smoothstep = (value: number): number => value * value * (3 - 2 * value);

const Screen = styled.div`
  position: relative;
  isolation: isolate;
  display: grid;
  place-items: center;
  width: min(560px, calc(100vw - 32px));
  min-height: 360px;
`;

const DotCanvas = styled.canvas<{ $isError: boolean }>`
  position: fixed;
  z-index: 0;
  top: 50%;
  left: 50%;
  width: ${DOT_FIELD_SIZE}px;
  height: ${DOT_FIELD_SIZE}px;
  color: ${({ $isError, theme }) => ($isError ? 'var(--color-destructive)' : theme.primary.blendToBackground(800))};
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

interface DotFieldProps {
  completionState: CompletionState;
}

const DotField: React.FC<DotFieldProps> = ({ completionState }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isComplete = completionState !== 'idle';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const completionStart = isComplete ? performance.now() : undefined;
    let animationFrame = 0;
    let lastFrame = -TWINKLE_FRAME_INTERVAL_MS;
    let recaptchaCenterY = 0;

    const resize = () => {
      const canvasTop = (window.innerHeight - DOT_FIELD_SIZE) / 2;
      recaptchaCenterY = (window.innerHeight - RECAPTCHA_CENTER_FROM_VIEWPORT_BOTTOM - canvasTop) / DOT_FIELD_SIZE;
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.round(DOT_FIELD_SIZE * pixelRatio);
      canvas.height = Math.round(DOT_FIELD_SIZE * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const draw = (time: number) => {
      context.clearRect(0, 0, DOT_FIELD_SIZE, DOT_FIELD_SIZE);
      const rippleProgress =
        completionStart === undefined ? -1 : (time - completionStart) / COMPLETION_RIPPLE_DURATION_MS;
      if (rippleProgress >= 1 + COMPLETION_RIPPLE_OUT_FEATHER) return;

      context.fillStyle = window.getComputedStyle(canvas).color;

      for (let row = 0; row < DOT_FIELD_LINES; row += 1) {
        const dotY = row * DOT_GRID_SIZE + DOT_GRID_SIZE / 2;
        const y = dotY / DOT_FIELD_SIZE;

        for (let column = 0; column < DOT_FIELD_LINES; column += 1) {
          const dotX = column * DOT_GRID_SIZE + DOT_GRID_SIZE / 2;
          const x = dotX / DOT_FIELD_SIZE;
          const distanceFromCenter = Math.hypot((x - 0.5) * 2, (y - 0.5) * 2);
          if (distanceFromCenter >= 1) continue;

          const centerProgress = Math.min(1, Math.max(0, distanceFromCenter - 0.05) / 0.5);
          const centerScale = smoothstep(centerProgress);
          const recaptchaScale = Math.min(1, Math.max(0, (recaptchaCenterY - y) / 0.1));
          const outerScale = Math.max(0, 1 - Math.pow(distanceFromCenter, 1.5));
          const diameter = (1 + outerScale * 5.5) * Math.min(centerScale, recaptchaScale);
          if (diameter < 0.1) continue;

          const delaySeed = dotRandom(row, column, 1);
          const durationSeed = dotRandom(row, column, 2);
          const peakSeed = dotRandom(row, column, 3);
          const widthSeed = dotRandom(row, column, 4);
          const duration = 4 + durationSeed * 4;
          const phase = ((time / 1000 + delaySeed * 8) % duration) / duration;
          const twinkleHalfWidth = 0.04 + widthSeed * 0.05;
          const fade =
            phase < 0.5 - twinkleHalfWidth || phase > 0.5 + twinkleHalfWidth
              ? 0
              : phase <= 0.5
                ? (phase - (0.5 - twinkleHalfWidth)) / twinkleHalfWidth
                : (0.5 + twinkleHalfWidth - phase) / twinkleHalfWidth;
          const easedFade = (1 - Math.cos(fade * Math.PI)) / 2;
          const peakOpacity = 0.65 + peakSeed * 0.3;
          const twinkleOpacity = reducedMotion ? 0.55 : 0.22 + easedFade * (peakOpacity - 0.22);
          const distanceUntilRipple = distanceFromCenter - rippleProgress;
          const rippleOutScale =
            completionStart === undefined || distanceUntilRipple > 0
              ? 1
              : 1 - smoothstep(Math.min(1, -distanceUntilRipple / COMPLETION_RIPPLE_OUT_FEATHER));

          context.globalAlpha = twinkleOpacity * rippleOutScale;
          context.beginPath();
          context.arc(dotX, dotY, diameter / 2, 0, Math.PI * 2);
          context.fill();
        }
      }

      context.globalAlpha = 1;
    };

    const animate = (time: number) => {
      if (time - lastFrame >= TWINKLE_FRAME_INTERVAL_MS) {
        draw(time);
        lastFrame = time;
      }
      animationFrame = window.requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    if (reducedMotion) {
      draw(0);
    } else {
      animationFrame = window.requestAnimationFrame(animate);
    }

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [completionState, isComplete]);

  return <DotCanvas ref={canvasRef} $isError={completionState === 'error'} aria-hidden="true" />;
};

const AuthenticationContent = styled.div<{ $isComplete: boolean }>`
  position: fixed;
  z-index: 1;
  top: 50%;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${({ $isComplete }) => ($isComplete ? 0 : 1)};
  text-align: center;
  transform: translate(-50%, -50%);
  transition: opacity 400ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MarkWrapper = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
`;

const AuthenticationLogo = styled(Logo)`
  --logo-color: ${({ theme }) => theme.secondary.background};
`;

const AuthenticationText = styled.div`
  position: relative;
  width: min(440px, calc(100vw - 48px));
  padding: 16px 48px 24px;
  box-sizing: border-box;
`;

const AuthenticationDescription = styled(Paragraph)`
  margin-top: 8px;
`;

const errorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallback;
};

export const PageMagicLink: React.FC = () => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const { setSession, loadSession, isSignedIn, params } = useSessionStore();
  const executeReCAPTCHA = useReCAPTCHA();
  const navigate = useNavigate();
  const { code: pathCode } = useParams<keyof Params>() as Params;
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code')?.trim() || pathCode;
  const shouldResumeOnboarding = searchParams.get(ONBOARDING_QUERY_PARAM) === '1';
  const [completionState, setCompletionState] = useState<CompletionState>('idle');
  const isComplete = completionState !== 'idle';
  const authenticationPreview = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('preview') : null;
  const isAuthenticationPreview =
    authenticationPreview === 'authenticating' ||
    authenticationPreview === 'transition' ||
    authenticationPreview === 'error-transition';

  useEffect(() => {
    if (authenticationPreview !== 'transition' && authenticationPreview !== 'error-transition') return;

    const timeout = window.setTimeout(
      () => setCompletionState(authenticationPreview === 'error-transition' ? 'error' : 'success'),
      500
    );
    return () => window.clearTimeout(timeout);
  }, [authenticationPreview]);

  useLayoutEffect(() => {
    let referrerPolicy = document.getElementById(REFERRER_POLICY_META_ID) as HTMLMetaElement | null;

    if (!referrerPolicy) {
      referrerPolicy = document.createElement('meta');
      referrerPolicy.id = REFERRER_POLICY_META_ID;
      referrerPolicy.name = 'referrer';
      referrerPolicy.content = 'no-referrer';
      document.head.appendChild(referrerPolicy);
    }

    return () => referrerPolicy?.remove();
  }, []);

  const plaidAuthFlow = useMemo(() => getActivePlaidAuthFlow(params), [params]);

  const returnToLoginWithError = (error: unknown, fallback: string) => {
    addDangerNotification({
      content: errorMessage(error, fallback),
      display: 'page-login',
      timeout: -1,
    });
    navigate(plaidAuthFlow ? ROUTE.PLAID_LOGIN : ROUTE.LOGIN, { replace: true });
  };

  const magicLinkMutation = useMutation({
    mutationFn: async (token: string) => {
      const minimumDuration = delay(MINIMUM_AUTHENTICATION_DURATION_MS);
      try {
        const reCaptchaToken = await executeReCAPTCHA(RecaptchaAction.MagicLink);
        const { data: response } = await loginConfirmLoginEmailChallenge({
          body: { token },
          headers: { 'g-recaptcha-response': reCaptchaToken },
          throwOnError: true,
        });

        return {
          challengeName: ChallengeName[response.challenge_name],
          dashboardUserId: response.dashboard_user_id,
          defaultPlatformId: response.default_platform_id,
          emailConfirmationStatus: EmailConfirmationStatus[response.email_confirmation_status],
          mfaStatus: MFAStatus[response.mfa_status],
          phoneNumber: response.phone_number,
          sessionId: response.session_id,
        };
      } finally {
        await minimumDuration;
      }
    },
    onSuccess: async (response) => {
      setCompletionState('success');

      // MFA follows the email step-up, so the session is not usable yet — hand the challenge back to the
      // login page rather than attempting to sign in.
      if (response.challengeName && response.challengeName !== ChallengeName.CHALLENGE_NONE) {
        const resumeMFA: ResumeMFAState = {
          challengeName: response.challengeName,
          phoneNumber: response.phoneNumber,
          sessionId: response.sessionId,
        };
        await delay(COMPLETION_ANIMATION_DURATION_MS);
        navigate(plaidAuthFlow ? ROUTE.PLAID_LOGIN : ROUTE.LOGIN, { replace: true, state: { resumeMFA } });
        return;
      }

      setSession(response.sessionId, response.dashboardUserId, {
        loginAuthMethod: LoginAuthMethod.PASSWORD,
        mfaStatus: response.mfaStatus,
      });

      const loadSessionResult = loadSession().then(
        (resolvedSessionState) => ({ sessionState: resolvedSessionState, loadSessionError: null }),
        (caughtLoadSessionError: unknown) => ({
          sessionState: null,
          loadSessionError: caughtLoadSessionError,
        })
      );
      await delay(COMPLETION_ANIMATION_DURATION_MS);
      const { sessionState, loadSessionError } = await loadSessionResult;
      if (loadSessionError !== null) {
        returnToLoginWithError(loadSessionError, 'Unable to sign in. Please try again.');
        return;
      }

      if (plaidAuthFlow) {
        navigate(ROUTE.PLAID_SELECT, { replace: true, state: { from: 'auth' } });
        return;
      }

      const hasPlatformAccess = !!sessionState?.currentPlatform;

      // MFA setup runs before onboarding, carrying the flag so the questionnaire resumes afterwards.
      if (shouldPromptMFASetup(response.mfaStatus, LoginAuthMethod.PASSWORD) && hasPlatformAccess) {
        navigate(shouldResumeOnboarding ? `${ROUTE.SETUP_MFA}?${ONBOARDING_QUERY_PARAM}=1` : ROUTE.SETUP_MFA, {
          replace: true,
          state: { from: 'auth' },
        });
        return;
      }

      if (shouldResumeOnboarding) {
        navigate(ROUTE.REGISTER_FINANCIAL_PRODUCTS, { replace: true });
        return;
      }

      navigate(APP_ROUTE.ROOT, { replace: true, state: { from: 'auth' } });
    },
    onError: async (error: unknown, token: string) => {
      if (!wasRejectedByServer(error)) {
        releaseTokenRedemption(token);
      }
      setCompletionState('error');
      await delay(COMPLETION_ANIMATION_DURATION_MS);
      if (isSignedIn()) {
        navigate(plaidAuthFlow ? ROUTE.PLAID_SELECT : APP_ROUTE.ROOT, { replace: true, state: { from: 'auth' } });
        return;
      }
      returnToLoginWithError(error, 'Unable to confirm this sign in. Please try again.');
    },
    retry: false,
  });

  const { mutate } = magicLinkMutation;
  useEffect(() => {
    if (isAuthenticationPreview) {
      return;
    }

    if (!code) {
      navigate(plaidAuthFlow ? ROUTE.PLAID_LOGIN : ROUTE.LOGIN, { replace: true });
      return;
    }

    if (!claimTokenRedemption(code)) {
      if (isSignedIn()) {
        navigate(plaidAuthFlow ? ROUTE.PLAID_SELECT : APP_ROUTE.ROOT, { replace: true });
        return;
      }
      navigate(plaidAuthFlow ? ROUTE.PLAID_LOGIN : ROUTE.LOGIN, { replace: true });
      return;
    }

    mutate(code);
  }, [code, isAuthenticationPreview, isSignedIn, mutate, navigate, plaidAuthFlow]);

  return (
    <Screen role="status" aria-busy={!isComplete} aria-live="polite">
      <DotField completionState={completionState} />
      <AuthenticationContent $isComplete={isComplete}>
        <MarkWrapper aria-hidden="true">
          <AuthenticationLogo size="24px" />
        </MarkWrapper>
        <AuthenticationText>
          <Text size={16} weight="semibold">
            Authenticating
          </Text>
          <AuthenticationDescription>Confirming your secure sign-in link…</AuthenticationDescription>
        </AuthenticationText>
      </AuthenticationContent>
    </Screen>
  );
};
